import React, { useEffect, useMemo, useState } from "react";
import CompanyProfile from "./CompanyProfile";
import { prettifyStatusLabel } from "./prettifyStatusLabel";

import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

const decisionColor = {
  interesting: "#0a7f5a",
  not_interesting: "#b42318",
  uncertain: "#b54708",
};

function JsonBlock({ data }) {
  if (!data) return null;

  return (
    <pre
      style={{
        margin: 0,
        background: "#f1eee8",
        padding: "18px",
        borderRadius: "14px",
        overflowX: "auto",
        fontSize: "12px",
        lineHeight: 1.55,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        border: "1px solid rgba(17,17,17,0.08)",
      }}
    >
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function Section({ title, children, right }) {
  return (
    <section
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "24px",
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "28px",
            lineHeight: 1.1,
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function MetricCard({ label, value }) {
  return (
    <div
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "22px",
        minHeight: "120px",
      }}
    >
      <div
        style={{
          fontSize: "12px",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "#5f5a52",
          marginBottom: "12px",
          fontFamily: 'Arial, Helvetica, sans-serif',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "44px",
          lineHeight: 1,
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: "12px",
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        marginBottom: "8px",
        color: "#5f5a52",
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}
    >
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "12px 14px",
        borderRadius: "12px",
        border: "1px solid rgba(17,17,17,0.14)",
        background: "#fff",
        fontSize: "14px",
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxSizing: "border-box",
      }}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: "12px 14px",
        borderRadius: "12px",
        border: "1px solid rgba(17,17,17,0.14)",
        background: "#fff",
        fontSize: "14px",
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxSizing: "border-box",
      }}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function statusLabel(status) {
  if (!status) return "Okänd";
  return prettifyStatusLabel(status);
}

function triggerScoreTone(score) {
  const n = Number(score);
  if (Number.isNaN(n)) return { color: "#111", fontWeight: 400 };
  if (n > 0) return { color: "#0a5c40", fontWeight: 500 };
  if (n < 0) return { color: "#9a9288", fontWeight: 400 };
  return { color: "#111", fontWeight: 400 };
}

function statusTone(status) {
  switch (status) {
    case "company_candidates_found":
      return { bg: "#ece8df", fg: "#111" };
    case "analyst_queue":
      return { bg: "#efe9dd", fg: "#111" };
    case "duplicate_trigger":
      return { bg: "#f1ece5", fg: "#6b6258" };
    case "no_valid_companies":
      return { bg: "#f3efe9", fg: "#6b6258" };
    case "company_candidates_sparse":
      return { bg: "#f3ede3", fg: "#6b6258" };
    default:
      return { bg: "#f3f0ea", fg: "#333" };
  }
}

function TriggerList() {
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [triggers, setTriggers] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [selectedFingerprint, setSelectedFingerprint] = useState(null);
  const [history, setHistory] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [overview, setOverview] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [onlyWithCompanies, setOnlyWithCompanies] = useState(true);
  const [excludeDuplicates, setExcludeDuplicates] = useState(true);
  const [collapseSimilar, setCollapseSimilar] = useState(true);

  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);

  const loadTriggers = () => {
    setLoadingList(true);
    setError(null);

    const params = new URLSearchParams({
      page: "1",
      page_size: "100",
    });

    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (statusFilter) params.append("status", statusFilter);
    if (onlyWithCompanies) params.append("has_companies", "true");
    if (excludeDuplicates) params.append("exclude_duplicates", "true");
    if (collapseSimilar) params.append("collapse_similar", "true");

    Promise.all([
      apiFetch(`${getApiBase()}/triggers?${params.toString()}`).then((res) =>
        parseJsonOrExplain(res)
      ),
      apiFetch(`${getApiBase()}/statistics/overview`).then((res) =>
        parseJsonOrExplain(res)
      ),
    ])
      .then(([triggerData, overviewData]) => {
        // Triggers med workflow-status "rejected" ska aldrig visas på denna sida.
        const visibleTriggers = (Array.isArray(triggerData) ? triggerData : []).filter(
          (t) => String(t.latest_status || "").toLowerCase() !== "rejected"
        );
        setTriggers(visibleTriggers);
        setOverview(overviewData);
        setLoadingList(false);

        if (visibleTriggers.length > 0) {
          const stillExists = visibleTriggers.some(
            (t) => t.fingerprint === selectedFingerprint
          );

          if (!selectedFingerprint || !stillExists) {
            setSelectedFingerprint(visibleTriggers[0].fingerprint);
          }
        } else {
          setSelectedFingerprint(null);
          setSelectedTrigger(null);
          setHistory([]);
          setCompanies([]);
        }
      })
      .catch((err) => {
        setError(err.message || "Fel vid hämtning");
        setLoadingList(false);
      });
  };

  useEffect(() => {
    loadTriggers();
  }, []);

  useEffect(() => {
    if (!selectedFingerprint) return;

    setLoadingDetail(true);
    setError(null);

    Promise.all([
      apiFetch(`${getApiBase()}/triggers/${selectedFingerprint}`).then((res) =>
        parseJsonOrExplain(res)
      ),
      apiFetch(`${getApiBase()}/triggers/${selectedFingerprint}/process_history`).then(
        (res) => parseJsonOrExplain(res)
      ),
      apiFetch(`${getApiBase()}/triggers/${selectedFingerprint}/companies`).then((res) =>
        parseJsonOrExplain(res)
      ),
    ])
      .then(([triggerData, historyData, companiesData]) => {
        setSelectedTrigger(triggerData);
        setHistory(historyData);
        setCompanies(companiesData);
        setLoadingDetail(false);
      })
      .catch((err) => {
        setError(err.message || "Fel vid hämtning av detaljer");
        setLoadingDetail(false);
      });
  }, [selectedFingerprint]);

  const filteredTriggers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return triggers.filter((t) => {
      if (!q) return true;

      const title = t.raw_json?.title?.toLowerCase() || "";
      const category = t.raw_json?.trigger_category?.toLowerCase() || "";
      const jurisdiction = t.raw_json?.jurisdiction?.toLowerCase() || "";
      const fingerprint = t.fingerprint?.toLowerCase() || "";
      const latestStatus = t.latest_status?.toLowerCase() || "";

      return (
        title.includes(q) ||
        category.includes(q) ||
        jurisdiction.includes(q) ||
        fingerprint.includes(q) ||
        latestStatus.includes(q)
      );
    });
  }, [triggers, searchTerm]);

  const selectedStatus =
    history.length > 0 ? history[history.length - 1]?.status : "-";

  const totalCompanyLinks = triggers.reduce(
    (sum, t) => sum + Number(t.company_count || 0),
    0
  );

  return (
    <>
      <div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          <MetricCard label="Visade triggers" value={triggers.length} />
          <MetricCard label="Bolagskopplingar" value={totalCompanyLinks} />
          <MetricCard
            label="Totala triggers"
            value={overview?.total_triggers ?? "–"}
          />
          <MetricCard
            label="Analyst queue"
            value={overview?.analyst_queue ?? "–"}
          />
        </div>

        <Section title="Filter och urval">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
              gap: "14px",
              alignItems: "end",
              marginBottom: "14px",
            }}
          >
            <div>
              <Label>Sök</Label>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Titel, kategori, status, fingerprint"
              />
            </div>

            <div>
              <Label>Datum från</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div>
              <Label>Datum till</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <div>
              <Label>Status</Label>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: "", label: "Alla" },
                  {
                    value: "company_candidates_found",
                    label: prettifyStatusLabel("company_candidates_found"),
                  },
                  {
                    value: "company_candidates_sparse",
                    label: prettifyStatusLabel("company_candidates_sparse"),
                  },
                  {
                    value: "analyst_queue",
                    label: prettifyStatusLabel("analyst_queue"),
                  },
                  {
                    value: "no_valid_companies",
                    label: prettifyStatusLabel("no_valid_companies"),
                  },
                  {
                    value: "duplicate_trigger",
                    label: prettifyStatusLabel("duplicate_trigger"),
                  },
                  { value: "new", label: prettifyStatusLabel("new") },
                ]}
              />
            </div>

            <div>
              <button
                onClick={loadTriggers}
                style={{
                  padding: "12px 18px",
                  borderRadius: "999px",
                  border: "1px solid #111",
                  background: "#111",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontFamily: 'Arial, Helvetica, sans-serif',
                }}
              >
                Uppdatera
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "18px",
              flexWrap: "wrap",
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: "14px",
            }}
          >
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={onlyWithCompanies}
                onChange={(e) => setOnlyWithCompanies(e.target.checked)}
              />
              Bara triggers med bolag
            </label>

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={excludeDuplicates}
                onChange={(e) => setExcludeDuplicates(e.target.checked)}
              />
              Exkludera workflow-duplicates
            </label>

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={collapseSimilar}
                onChange={(e) => setCollapseSimilar(e.target.checked)}
              />
              Slå ihop liknande titlar
            </label>
          </div>
        </Section>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "420px minmax(0, 1fr)",
            gap: "20px",
            alignItems: "start",
          }}
        >
          <Section
            title="Triggers"
            right={
              <div
                style={{
                  fontSize: "13px",
                  color: "#5f5a52",
                  fontFamily: 'Arial, Helvetica, sans-serif',
                }}
              >
                {loadingList ? "Laddar..." : `${filteredTriggers.length} träffar`}
              </div>
            }
          >
            {error && !selectedTrigger && (
              <p style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                {error}
              </p>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {filteredTriggers.map((t) => {
                const isActive = t.fingerprint === selectedFingerprint;
                const companyCount = Number(t.company_count || 0);
                const interestingN = Number(t.interesting_count || 0);
                const notInterestingN = Number(t.not_interesting_count || 0);
                const uncertainN = Number(t.uncertain_count || 0);
                const scoreN = Number(
                  t.trigger_score !== undefined && t.trigger_score !== null
                    ? t.trigger_score
                    : interestingN * 3 + uncertainN - notInterestingN
                );
                const scoreStyle = triggerScoreTone(scoreN);
                const tone = statusTone(t.latest_status);

                return (
                  <button
                    key={t.fingerprint}
                    onClick={() => setSelectedFingerprint(t.fingerprint)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "16px",
                      borderRadius: "18px",
                      border: isActive
                        ? "1px solid #111"
                        : "1px solid rgba(17,17,17,0.10)",
                      background: isActive ? "#f1eee8" : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "20px",
                        lineHeight: 1.2,
                        marginBottom: "8px",
                      }}
                    >
                      {t.raw_json?.title || "Ingen titel"}
                    </div>

                    <div
                      style={{
                        fontSize: "13px",
                        color: "#5f5a52",
                        marginBottom: "8px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      {formatDate(t.created_at)}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                        marginBottom: "10px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      <span
                        style={{
                          fontSize: "12px",
                          padding: "4px 8px",
                          borderRadius: "999px",
                          background: "#f3f0ea",
                          border: "1px solid rgba(17,17,17,0.08)",
                        }}
                      >
                        {t.raw_json?.trigger_category || "Okänd kategori"}
                      </span>

                      <span
                        style={{
                          fontSize: "12px",
                          padding: "4px 8px",
                          borderRadius: "999px",
                          background: "#f3f0ea",
                          border: "1px solid rgba(17,17,17,0.08)",
                        }}
                      >
                        {t.raw_json?.jurisdiction || "Okänd jurisdiktion"}
                      </span>

                      <span
                        style={{
                          fontSize: "12px",
                          padding: "4px 8px",
                          borderRadius: "999px",
                          background: tone.bg,
                          color: tone.fg,
                          border: "1px solid rgba(17,17,17,0.08)",
                        }}
                      >
                        {statusLabel(t.latest_status)}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: "13px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                        lineHeight: 1.45,
                        color: companyCount > 0 ? "#111" : "#7a746c",
                      }}
                    >
                      <div>Kopplade bolag: {companyCount}</div>
                      <div
                        style={{
                          marginTop: "6px",
                          color: "#5f5a52",
                          fontSize: "12px",
                        }}
                      >
                        Intressant: {interestingN}
                        {" · "}
                        Ej intressant: {notInterestingN}
                        {" · "}
                        Osäker: {uncertainN}
                        {" · "}
                        <span
                          style={{
                            color: scoreStyle.color,
                            fontWeight: scoreStyle.fontWeight,
                          }}
                        >
                          Poäng: {scoreN}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}

              {!loadingList && filteredTriggers.length === 0 && (
                <div
                  style={{
                    padding: "18px",
                    borderRadius: "18px",
                    background: "#fff",
                    border: "1px solid rgba(17,17,17,0.10)",
                    fontFamily: 'Arial, Helvetica, sans-serif',
                  }}
                >
                  Inga triggers matchar filtret.
                </div>
              )}
            </div>
          </Section>

          <div>
            <Section
              title="Triggerdetalj"
              right={
                <div
                  style={{
                    fontSize: "13px",
                    color: "#5f5a52",
                    fontFamily: 'Arial, Helvetica, sans-serif',
                  }}
                >
                  Status:{" "}
                  {!selectedStatus || selectedStatus === "-"
                    ? "-"
                    : prettifyStatusLabel(selectedStatus)}
                </div>
              }
            >
              {loadingDetail && (
                <p style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                  Laddar detaljvy...
                </p>
              )}

              {error && (
                <p style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                  {error}
                </p>
              )}

              {selectedTrigger && !loadingDetail && (
                <>
                  <div
                    style={{
                      marginBottom: "20px",
                      paddingBottom: "20px",
                      borderBottom: "1px solid rgba(17,17,17,0.10)",
                    }}
                  >
                    <h2
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "42px",
                        lineHeight: 1.02,
                        fontWeight: 500,
                        letterSpacing: "-0.03em",
                      }}
                    >
                      {selectedTrigger.raw_json?.title || "Ingen titel"}
                    </h2>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      <span>
                        Kategori: {selectedTrigger.raw_json?.trigger_category || "-"}
                      </span>
                      <span>•</span>
                      <span>
                        Jurisdiktion: {selectedTrigger.raw_json?.jurisdiction || "-"}
                      </span>
                      <span>•</span>
                      <span>
                        Strength: {selectedTrigger.raw_json?.strength_score ?? "-"}
                      </span>
                      <span>•</span>
                      <span>
                        Confidence: {selectedTrigger.raw_json?.confidence ?? "-"}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      marginBottom: "18px",
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "18px",
                        padding: "16px",
                        border: "1px solid rgba(17,17,17,0.10)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "10px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Why now
                      </div>
                      <div
                        style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                        }}
                      >
                        {selectedTrigger.raw_json?.why_now || "-"}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "18px",
                        padding: "16px",
                        border: "1px solid rgba(17,17,17,0.10)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "10px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Fingerprint
                      </div>
                      <div
                        style={{
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                          wordBreak: "break-all",
                        }}
                      >
                        {selectedTrigger.fingerprint}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "18px",
                      padding: "18px",
                      border: "1px solid rgba(17,17,17,0.10)",
                      marginBottom: "18px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: "#5f5a52",
                        marginBottom: "10px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      Causal chain
                    </div>
                    <div
                      style={{
                        fontFamily: 'Arial, Helvetica, sans-serif',
                        lineHeight: 1.7,
                      }}
                    >
                      {selectedTrigger.raw_json?.causal_chain || "-"}
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "18px",
                      padding: "18px",
                      border: "1px solid rgba(17,17,17,0.10)",
                      marginBottom: "18px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: "#5f5a52",
                        marginBottom: "8px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      Facts
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "18px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                        lineHeight: 1.6,
                      }}
                    >
                      {(selectedTrigger.raw_json?.facts || []).map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "18px",
                      padding: "18px",
                      border: "1px solid rgba(17,17,17,0.10)",
                      marginBottom: "18px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: "#5f5a52",
                        marginBottom: "10px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      Sources
                    </div>

                    <div style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "6px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Primary
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "18px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                        }}
                      >
                        {(selectedTrigger.raw_json?.primary_sources || []).map(
                          (s, i) => (
                            <li key={i}>
                              <a
                                href={s}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {s}
                              </a>
                            </li>
                          )
                        )}
                      </ul>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "6px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Secondary
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "18px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                        }}
                      >
                        {(selectedTrigger.raw_json?.secondary_sources || []).map(
                          (s, i) => (
                            <li key={i}>
                              <a
                                href={s}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {s}
                              </a>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "18px",
                      padding: "18px",
                      border: "1px solid rgba(17,17,17,0.10)",
                      marginBottom: "18px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: "#5f5a52",
                        marginBottom: "8px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      Why it may be noise
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "18px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                        lineHeight: 1.6,
                      }}
                    >
                      {(selectedTrigger.raw_json?.why_it_may_be_noise || []).map(
                        (n, i) => (
                          <li key={i}>{n}</li>
                        )
                      )}
                    </ul>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "16px",
                      marginBottom: "18px",
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "18px",
                        padding: "16px",
                        border: "1px solid rgba(17,17,17,0.10)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "10px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        What is bought
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "18px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                        }}
                      >
                        {(selectedTrigger.raw_json?.what_is_bought || []).map(
                          (x, i) => (
                            <li key={i}>{x}</li>
                          )
                        )}
                      </ul>
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "18px",
                        padding: "16px",
                        border: "1px solid rgba(17,17,17,0.10)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "10px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Buyers
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "18px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                        }}
                      >
                        {(selectedTrigger.raw_json?.buyers || []).map((x, i) => (
                          <li key={i}>{x}</li>
                        ))}
                      </ul>
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "18px",
                        padding: "16px",
                        border: "1px solid rgba(17,17,17,0.10)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "10px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Investment angles
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "18px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          lineHeight: 1.6,
                        }}
                      >
                        {(selectedTrigger.raw_json?.investment_angles || []).map(
                          (x, i) => (
                            <li key={i}>{x}</li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      marginBottom: "18px",
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "18px",
                        padding: "18px",
                        border: "1px solid rgba(17,17,17,0.10)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "14px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Processhistorik
                      </div>

                      {history.length === 0 ? (
                        <div style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                          Ingen processhistorik hittades.
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                          }}
                        >
                          {history.map((row, i) => (
                            <div
                              key={i}
                              style={{
                                paddingLeft: "14px",
                                borderLeft: "2px solid #111",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "18px",
                                  marginBottom: "4px",
                                }}
                              >
                                {prettifyStatusLabel(row.status || "-")}
                              </div>
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: "#5f5a52",
                                  marginBottom: row.decision ? "4px" : 0,
                                  fontFamily: 'Arial, Helvetica, sans-serif',
                                }}
                              >
                                {formatDate(row.created_at)}
                              </div>
                              {row.decision && (
                                <div
                                  style={{
                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                  }}
                                >
                                  Beslut: {prettifyStatusLabel(row.decision)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "18px",
                        padding: "18px",
                        border: "1px solid rgba(17,17,17,0.10)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          color: "#5f5a52",
                          marginBottom: "14px",
                          fontFamily: 'Arial, Helvetica, sans-serif',
                        }}
                      >
                        Kopplade bolag
                      </div>

                      {companies.length === 0 ? (
                        <div style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                          Inga bolag kopplade till triggern ännu.
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                          }}
                        >
                          {companies.map((c, i) => (
                            <div
                              key={`${c.company_id}-${i}`}
                              onClick={() => setSelectedCompanyId(c.company_id)}
                              style={{
                                padding: "12px",
                                border: "1px solid rgba(17,17,17,0.1)",
                                borderRadius: "12px",
                                cursor: "pointer",
                                background: "#fff",
                              }}
                            >
                              <div style={{ fontWeight: 500, marginBottom: "4px" }}>
                                {c.company_name}
                              </div>

                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  fontFamily: 'Arial, Helvetica, sans-serif',
                                  marginBottom: "4px",
                                }}
                              >
                                {c.company_id}
                              </div>

                              <div
                                style={{
                                  fontSize: "12px",
                                  fontFamily: 'Arial, Helvetica, sans-serif',
                                }}
                              >
                                <span
                                  style={{
                                    color:
                                      decisionColor[c.ic_decision] || "#666",
                                  }}
                                >
                                  {prettifyStatusLabel(c.ic_decision || "–")}
                                </span>
                                {" • "}
                                {prettifyStatusLabel(c.status || "–")}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "18px",
                      padding: "18px",
                      border: "1px solid rgba(17,17,17,0.10)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: "#5f5a52",
                        marginBottom: "12px",
                        fontFamily: 'Arial, Helvetica, sans-serif',
                      }}
                    >
                      Rå JSON
                    </div>
                    <JsonBlock data={selectedTrigger.raw_json} />
                  </div>
                </>
              )}

              {!selectedTrigger && !loadingDetail && (
                <p style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                  Ingen trigger vald.
                </p>
              )}
            </Section>
          </div>
        </div>
      </div>

      {selectedCompanyId && (
        <CompanyProfile
          companyId={selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
        />
      )}
    </>
  );
}

export default TriggerList;
